import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Typography as TypographyMui } from '@mui/material'
import {
  Button,
  Drawer,
  DrawerProps,
  Dropdown,
  Layout,
  Menu,
  theme,
  Form,
  Input,
  message,
  Tooltip,
} from 'antd'
import useMediaQuery from 'beautiful-react-hooks/useMediaQuery'
import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'

import { Box, ThemeProvider } from '@mui/material'
import dayjs from 'dayjs'
import { ONLY_DATE_FORMAT } from '../../constants/global'
import { ImageLinks } from '../../constants/image-link'
import PageUrls from '../../constants/page-urls'
import { useUser } from '../../hooks/useUser'
import { UserRole } from '../../types/user'
import { onMerchantSwitchedInOtherTab } from '../../utils/cache'
import UserRoleTag from '../../pages/Users/components/UserRoleTag'
import { createTheme } from '../../themes'
import { AbilityContext } from '../Ability'
import SplashLoadingScreen from '../Loading/SplashLoadingScreen'
import MerchantSwitchModal from '../MerchantSwitch/MerchantSwitchModal'
import Modal from '../Modal'
import { MenuItems } from './Routes'
import {
  openModal,
  closeModal,
} from '../../../store/reducers/modal/modal.slice'
import { axiosInstance } from '../../hooks/useAxios'

const { Header, Sider, Content } = Layout

const drawerProps = {
  placement: 'left',
  width: 220,
} as DrawerProps

interface ISiderOrDrawer {
  collapsed: boolean
  toggle: () => void
  children: React.ReactNode
}

const SignOutMenu = () => {
  const navigate = useNavigate()

  const handleClickSignOut = () => {
    localStorage.clear()
    window.location.href = '/login?logout=true'
  }

  return (
    <Button
      type="text"
      style={{ background: 'transparent' }}
      onClick={handleClickSignOut}
    >
      ออกจากระบบ
    </Button>
  )
}

const ChangePasswordModal = () => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()

  const handleSubmit = async (values: {
    newPassword: string
    confirmPassword: string
  }) => {
    try {
      if (values.newPassword !== values.confirmPassword) {
        message.error('รหัสผ่านไม่ตรงกัน')
        return
      }

      await axiosInstance.put('/users/changepassword', {
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      })

      message.success('เปลี่ยนรหัสผ่านสำเร็จ')
      dispatch(closeModal())
      form.resetFields()
    } catch (error) {
      message.error('เกิดข้อผิดพลาดในการเปลี่ยนรหัสผ่าน')
    }
  }

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      <Form.Item
        name="newPassword"
        label="รหัสผ่านใหม่"
        rules={[
          { required: true, message: 'กรุณากรอกรหัสผ่านใหม่' },
          { min: 6, message: 'รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร' },
        ]}
      >
        <Input.Password />
      </Form.Item>
      <Form.Item
        name="confirmPassword"
        label="ยืนยันรหัสผ่านใหม่"
        rules={[
          { required: true, message: 'กรุณายืนยันรหัสผ่านใหม่' },
          { min: 6, message: 'รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร' },
        ]}
      >
        <Input.Password />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit">
          บันทึก
        </Button>
      </Form.Item>
    </Form>
  )
}

const ChangePasswordMenu = () => {
  const dispatch = useDispatch()

  const handleClick = () => {
    dispatch(
      openModal({
        title: 'เปลี่ยนรหัสผ่าน',
        content: <ChangePasswordModal />,
      }),
    )
  }

  return (
    <Button type="link" onClick={handleClick}>
      เปลี่ยนรหัสผ่าน
    </Button>
  )
}

const settings = {
  colorPreset: 'green',
  contrast: 'normal',
  direction: 'ltr',
  layout: 'vertical',
  navColor: 'evident',
  paletteMode: 'light',
  responsiveFontSizes: true,
  stretch: false,
}

const themeMui = createTheme({
  colorPreset: settings.colorPreset,
  contrast: settings.contrast,
  direction: settings.direction,
  paletteMode: settings.paletteMode,
  responsiveFontSizes: settings.responsiveFontSizes,
})

const SiderOrDrawer: FunctionComponent<ISiderOrDrawer> = (props) => {
  const { children, collapsed, toggle } = props
  const isSmall = useMediaQuery('(max-width: 1199px)')
  const LogoComponent = useCallback(
    () => (
      <div
        className="logo"
        style={isSmall ? { margin: '20px 16px' } : { margin: '10px 16px' }}
      >
        <LogoWrapper>
          {(isSmall && !collapsed) || (!isSmall && collapsed) ? (
            <img
              src={ImageLinks.LOGO}
              alt="smallLogoCt"
              height={50}
              width="100%"
            />
          ) : (
            <img
              src={ImageLinks.LOGO_HORIZONTAL}
              alt="logoCt"
              height={100}
              className="custom-logo"
            />
          )}
        </LogoWrapper>
      </div>
    ),
    [isSmall, collapsed],
  )

  return isSmall ? (
    <Drawer
      {...drawerProps}
      open={collapsed}
      closable={false}
      onClose={toggle}
      bodyStyle={{
        padding: 0,
        backgroundColor: '#0A1525',
      }}
      drawerStyle={{
        left: 0,
        width: 220,
      }}
    >
      <LogoComponent />
      {children}
    </Drawer>
  ) : (
    <>
      <Sider
        width={280}
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          display: 'absolute',
          zIndex: 1,
          height: '100vh',
          position: 'fixed',
        }}
      >
        <LogoComponent />
        <div
          className="custom-scrollbar"
          style={{
            overflow: 'auto',
            height: 'calc(100vh - 100px)',
            position: 'fixed',
            width: collapsed ? 80 : 280,
            paddingBottom: '50px',
          }}
        >
          {children}
        </div>
      </Sider>
      <div style={{ marginLeft: collapsed ? 80 : 280 }} />
    </>
  )
}

const DefaultLayout: React.FC<{
  children: React.ReactElement
  isPopup?: boolean
}> = ({ children, isPopup = false }) => {
  const { user, merchant, loading } = useUser()
  const [isSwitchMerchantOpen, setIsSwitchMerchantOpen] =
    useState<boolean>(false)
  // แท็บอื่นเปลี่ยนร้าน/ล็อกอินใหม่ → หน้านี้ยังโชว์ร้านเดิมทั้งที่หลังบ้านเปลี่ยนแล้ว
  useEffect(
    () => onMerchantSwitchedInOtherTab(() => window.location.reload()),
    [],
  )
  // ใช้ UserRole ไม่ใช่ EUserRole — EUserRole.SUPER_ADMIN = 'superAdmin' ไม่ตรงกับค่าจริง
  const isSuperAdmin = !!user?.roles?.includes(UserRole.SUPER_ADMIN)
  const ability = useContext(AbilityContext)
  const isSmall = useMediaQuery('(max-width: 1199px)')
  const location = useLocation()
  const [state, setState] = useState({
    collapsed: false,
    active: '',
  })
  const {
    token: { colorBgContainer },
  } = theme.useToken()
  const dispatch = useDispatch()

  const handleOnSelect = (e: any) => {
    setState({
      ...state,
      active: e.key,
    })
  }

  const handleToggleCollapse = () => {
    setState({
      ...state,
      collapsed: !state.collapsed,
    })
  }

  const calculateTimeLeft = () => {
    const now = dayjs()
    const expiry = dayjs(merchant.expiresDate)
    const diffInSeconds = expiry.diff(now, 'second')

    if (diffInSeconds <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }
    //add 0 in front of single digit numbers
    let hours: any = Math.floor((diffInSeconds % (24 * 60 * 60)) / (60 * 60))
    let minutes: any = Math.floor((diffInSeconds % (60 * 60)) / 60)
    let seconds: any = diffInSeconds % 60
    if (hours < 10) {
      hours = `0${hours}`
    }
    if (minutes < 10) {
      minutes = `0${minutes}`
    }

    if (seconds < 10) {
      seconds = `0${seconds}`
    }

    return {
      days: Math.floor(diffInSeconds / (24 * 60 * 60)).toLocaleString(),
      hours,
      minutes,
      seconds,
    }
  }

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft())

  const menuItems = useMemo(() => {
    return MenuItems(merchant, ability, user)
  }, [merchant, ability, user])

  // super admin เข้าร้านที่หมดอายุได้ (สลับไปช่วยร้าน) — ไม่นับถอยหลัง ไม่ตัดออก
  // แสดงวันหมดอายุแทน ส่วนผู้ใช้อื่นยังโดนตัดออกเมื่อร้านหมดอายุเหมือนเดิม
  const isMerchantExpired =
    !!merchant?.expiresDate && dayjs(merchant.expiresDate).isBefore(dayjs())

  useEffect(() => {
    if (isSuperAdmin) {
      return
    }
    const timer = setInterval(() => {
      const time = calculateTimeLeft()
      setTimeLeft(time)
      // console.log(time)
      if (
        time.days === 0 &&
        time.hours === 0 &&
        time.minutes === 0 &&
        time.seconds === 0 &&
        merchant?.expiresDate
      ) {
        localStorage.clear()
        localStorage.setItem('sessionExpired', 'true')
        window.location.href =
          '/login?sessionExpired=true&message=sessionExpired'
      }
    }, 1000)

    return () => clearInterval(timer)
  }, [merchant?.expiresDate, isSuperAdmin])

  useEffect(() => {
    if (location.pathname) {
      const active = menuItems
        ?.map((item: any) => {
          return item.children?.find((child: any) => {
            return location.pathname === child.key
          })
        })
        .filter(Boolean)

      if (active.length > 0) {
        setState({
          ...state,
          active: active?.[0]?.key ?? '',
        })
      }
    }
  }, [location.pathname])

  if (loading) {
    return <SplashLoadingScreen />
  }

  const mainMenu = menuItems
    ?.map((item) => {
      const { hidden, ...rest } = item
      if (hidden) {
        return null
      }
      return {
        ...rest,
      }
    })
    .filter(Boolean)

  return (
    <Wrapper>
      <Layout>
        {!isPopup && (
          <SiderOrDrawer
            collapsed={state.collapsed}
            toggle={handleToggleCollapse}
          >
            <Menu
              theme="dark"
              mode="inline"
              // inlineIndent={15}
              selectedKeys={[state.active]}
              onClick={handleOnSelect}
              items={mainMenu as any}
            />
          </SiderOrDrawer>
        )}
        <Layout className="site-layout">
          {!isPopup && (
            <Header
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0 20px',
                background: colorBgContainer,
              }}
            >
              {React.createElement(
                state.collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
                {
                  className: 'trigger',
                  onClick: handleToggleCollapse,
                },
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Box>
                  {isSuperAdmin ? (
                    <TypographyMui variant="body2" sx={{ textAlign: 'right' }}>
                      หมดอายุ:{' '}
                      {merchant?.expiresDate
                        ? dayjs(merchant.expiresDate).format(ONLY_DATE_FORMAT)
                        : '-'}
                      {isMerchantExpired && (
                        <>
                          {isSmall ? <br /> : ' '}
                          <TypographyMui
                            component="span"
                            variant="body2"
                            color="error"
                            fontWeight={700}
                          >
                            หมดอายุแล้ว
                          </TypographyMui>
                        </>
                      )}
                    </TypographyMui>
                  ) : (
                    <TypographyMui variant="body2" sx={{ textAlign: 'right' }}>
                      หมดอายุในอีก: {timeLeft.days} วัน {isSmall && <br />}
                      {timeLeft.hours}:{timeLeft.minutes}:{timeLeft.seconds}
                    </TypographyMui>
                  )}
                </Box>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'change-password',
                        label: <ChangePasswordMenu />,
                      },
                      {
                        key: 'sign-out',
                        label: <SignOutMenu />,
                      },
                    ],
                  }}
                >
                  <Button
                    icon={
                      <IconWrapper>
                        <UserOutlined />
                      </IconWrapper>
                    }
                    type="link"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: 'black',
                    }}
                  >
                    <span>{user?.username} </span>
                    <div style={{ marginLeft: 5 }}>
                      <UserRoleTag
                        merchant={user?.merchantPrefix ?? ''}
                        roles={user.roles as any}
                      />
                    </div>
                  </Button>
                </Dropdown>
                {isSuperAdmin && (
                  <>
                    <Tooltip title="เปลี่ยนร้าน">
                      <Button
                        type="text"
                        icon={<SwapOutlined />}
                        aria-label="เปลี่ยนร้าน"
                        onClick={() => setIsSwitchMerchantOpen(true)}
                      />
                    </Tooltip>
                    <MerchantSwitchModal
                      open={isSwitchMerchantOpen}
                      onClose={() => setIsSwitchMerchantOpen(false)}
                      username={user?.username ?? ''}
                      currentMerchant={user?.merchantPrefix}
                      currentMerchantName={merchant?.name}
                      currentMerchantStatus={merchant?.status}
                      currentMerchantExpired={isMerchantExpired}
                    />
                  </>
                )}
              </Box>
            </Header>
          )}
          <CustomContent>
            <ThemeProvider theme={themeMui}>
              <Modal />
              {children}
            </ThemeProvider>
          </CustomContent>
        </Layout>
      </Layout>
    </Wrapper>
  )
}

export default DefaultLayout

const Wrapper = styled.div`
  display: flex;
  min-height: 100vh;
  flex-direction: column;
  background: #fff;
  font-size: 16px;

  .a {
    color: #fff;
  }
`

const CustomContent = styled(Content)`
  min-height: 100vh;
  margin: 20px 0px;
  @media (max-width: 768px) {
    margin: 20px 2px;
  }
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  margin-right: 10px;
  border: solid 1px gray;
  border-radius: 100%;
`

const LogoWrapper = styled.div`
  display: flex;
  align-content: center;
  justify-content: center;
`
